// import "./App.scss";
import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import Appin from "./Appin";
import { AuthProvider } from "./auth/useAuth";
import CatalogContext from "./contexts/CatalogContext";
import "./global.scss";
import "./capacitor.scss";

import ReactGA from "react-ga4";
import { initAnalytics } from "./util/Analytics";
import { CustomerProvider } from "./auth/useCustomer";
import { CatalogSelectionContextProvider } from "./hooks/useCatalogSelectionContext";
import { LogProvider } from "./auth/useLog";
import { BuyingModeProvider } from "./contexts/BuyingModeContext";
import { isNative } from "./util/platform";

const queryClient = new QueryClient();

// ─── Analytics initialization ──────────────────────────────────────────────
//
// Two analytics systems coexist by design (see src/util/Analytics.js header):
//   • react-ga4 — marketing/UTM attribution only
//   • PostHog (via Analytics.js) — all behavioral / product analytics
// Do NOT add new GA4 events. Behavioral events go through PostHog only.
//
// Both are initialized once at module load (this file is the app entry point).
// Failures are isolated and logged so a misconfigured key never crashes the app.

const GA4_TRACKING_ID = "G-9B5G9811WM";

try {
	ReactGA.initialize(GA4_TRACKING_ID);
} catch (err) {
	// Don't let GA4 failure block PostHog or app startup
	console.warn("GA4 initialization failed (analytics-only impact):", err);
}

try {
	initAnalytics();
} catch (err) {
	// PostHog init shouldn't throw on bad config — Analytics.js silent-no-ops on
	// missing key — but defend against an SDK regression or malformed bundle.
	console.warn("PostHog initialization failed (analytics-only impact):", err);
}

function App() {

	const [catalogConfig, setCatalog] = React.useState(null);

	// Initialize Capacitor native plugins
	React.useEffect(() => {
		if (!isNative()) return;

		const initNativePlugins = async () => {
			// Enable native-only CSS rules
			document.body.classList.add('capacitor-native');

			const { SplashScreen } = await import('@capacitor/splash-screen');
			const { StatusBar, Style } = await import('@capacitor/status-bar');
			const { Keyboard } = await import('@capacitor/keyboard');

			// Hide splash screen once React is mounted
			await SplashScreen.hide({ fadeOutDuration: 300 });

			// Set status bar to light content (white icons on dark background)
			await StatusBar.setStyle({ style: Style.Light });

			// Keyboard resize behavior
			Keyboard.setResizeMode?.({ mode: 'body' });
		};

		initNativePlugins().catch(console.warn);
	}, []);

	return (
		<>
			<QueryClientProvider client={queryClient}>
				<AuthProvider>
					<LogProvider>
						<CustomerProvider>
							<BuyingModeProvider>
								<CatalogContext.Provider value={[catalogConfig, setCatalog]}>
									<CatalogSelectionContextProvider>
										<Appin />
									</CatalogSelectionContextProvider>
								</CatalogContext.Provider>
							</BuyingModeProvider>
						</CustomerProvider>
					</LogProvider>
				</AuthProvider>
				{/* <ReactQueryDevtools initialIsOpen={false} position="bottom-right" /> */}
			</QueryClientProvider>
		</>
	);
}

export default App;
